/**
 * The owner's resource-billing surface (Team tab):
 *   1. Usage & your cloud cost — measured meters vs the plan's allowances.
 *   2. What other organizations pay — the versioned price sheet, with live
 *      below-cost warnings against the plan's own rates (warn, never block;
 *      publishing a below-cost sheet requires an explicit confirm).
 *   3. Earnings — per consuming organization, net of the platform fee, with
 *      the honest margin line against the month's cloud cost.
 *
 * Renders nothing while resource billing is off (the legacy seat cards keep
 * the stage), and tells the parent through onEnabledChange so the legacy
 * SelfRegistrationCard can step aside when it is on.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Loader2 } from 'lucide-react'
import {
  formatMoney, formatUsage, getEarnings, getPriceSheet, getResourceUsage,
  publishPriceSheet,
  type BelowCostWarning, type Earnings, type PriceSheetInfo, type PublishSheetBody,
  type ResourceUsage, type SheetMeterRow,
} from '@/api/billing'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'

const METERS = ['users', 'rows', 'db_gb', 'file_gb', 'requests'] as const
const UNIT_LABEL: Record<string, string> = {
  users: 'user / mo', rows: '10k rows', db_gb: 'GB / mo',
  file_gb: 'GB / mo', requests: '100k req',
}

const card: React.CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
  borderRadius: 12, padding: '1rem 1.25rem', marginBottom: 16,
}
const h: React.CSSProperties = { fontSize: '0.9375rem', fontWeight: 600 }
const hint: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }

export default function ResourceBillingSection({ projectId, canManage, onEnabledChange }: {
  projectId: string
  canManage: boolean
  onEnabledChange?: (enabled: boolean) => void
}) {
  const [usage, setUsage] = useState<ResourceUsage | null>(null)
  const [sheetInfo, setSheetInfo] = useState<PriceSheetInfo | null>(null)
  const [earnings, setEarnings] = useState<Earnings | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const info = await getPriceSheet(projectId)
      setSheetInfo(info)
      onEnabledChange?.(info.enabled)
      if (!info.enabled) return
      const [u, e] = await Promise.all([
        getResourceUsage(projectId),
        getEarnings(projectId).catch(() => null),
      ])
      setUsage(u)
      setEarnings(e)
    } catch {
      // Resource billing endpoints unavailable — legacy cards keep the stage.
      onEnabledChange?.(false)
      setSheetInfo(null)
    } finally {
      setLoading(false)
    }
  }, [projectId, onEnabledChange])

  useEffect(() => { void load() }, [load])

  if (loading || !sheetInfo?.enabled) return null

  return (
    <div>
      {usage && <UsageCard usage={usage} />}
      {canManage && (
        <PricingCard projectId={projectId} info={sheetInfo} usage={usage} onPublished={load} />
      )}
      {canManage && earnings && earnings.organizations.length > 0 && (
        <EarningsCard earnings={earnings} />
      )}
    </div>
  )
}

// ── 1 · Usage & cloud cost ──────────────────────────────────────────────────

function UsageCard({ usage }: { usage: ResourceUsage }) {
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <span style={h}>This month&apos;s usage &amp; your cloud cost</span>
        <span style={hint}>{usage.plan_code} plan · whole app, all organizations</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto auto', gap: '8px 14px', alignItems: 'center', marginTop: 12 }}>
        {usage.lines.filter((l) => l.meter !== 'base_fee').map((l) => {
          const pct = l.allowance === null ? 0
            : l.allowance === 0 ? 100
              : Math.min(100, (l.qty / l.allowance) * 100)
          return (
            <FragmentRow key={l.meter}>
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{l.label}</span>
              <span style={{ height: 6, borderRadius: 99, background: 'var(--color-border)', overflow: 'hidden', display: 'block', minWidth: 80 }}>
                <span style={{
                  display: 'block', height: '100%', width: `${pct}%`, borderRadius: 99,
                  background: l.over_allowance ? 'var(--color-warning)' : 'var(--color-accent)',
                }} />
              </span>
              <span style={mono}>
                {fmtQty(l.meter, l.qty)}{l.allowance !== null ? ` / ${fmtQty(l.meter, l.allowance)} incl.` : ' · unlimited'}
              </span>
              <span style={{ ...mono, color: 'var(--color-text-secondary)', textAlign: 'right' }}>
                {l.action === 'block' ? 'hard cap'
                  : l.action === 'alert' ? 'alert only'
                    : formatMoney(l.amount_minor, usage.currency)}
              </span>
            </FragmentRow>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border-strong)', marginTop: 12, paddingTop: 8, alignItems: 'baseline' }}>
        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Your Twasta cloud cost this month</span>
        <span style={{ ...mono, fontSize: '1.0125rem', fontWeight: 600, color: 'var(--color-accent)' }}>{usage.total_display}</span>
      </div>
      <div style={{ ...hint, marginTop: 6 }}>
        Debited from the app wallet on the 1st. Each line is (usage over allowance ÷ unit block) × your plan rate, prorated.
      </div>
    </div>
  )
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

function fmtQty(meter: string, v: number): string {
  if (meter === 'db_gb' || meter === 'file_gb') return `${v.toFixed(1)} GB`
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1000) return `${Math.round(v / 1000)}k`
  return String(Math.round(v))
}

// ── 2 · Price sheet ─────────────────────────────────────────────────────────

type Mode = 'free' | 'per_user' | 'resource'

function PricingCard({ projectId, info, usage, onPublished }: {
  projectId: string
  info: PriceSheetInfo
  usage: ResourceUsage | null
  onPublished: () => void
}) {
  const sheet = info.sheet
  const [mode, setMode] = useState<Mode>(sheet?.mode ?? 'free')
  const [perUser, setPerUser] = useState<number>(sheet?.per_user_minor ?? 0)
  const [signupFee, setSignupFee] = useState<number>(sheet?.signup_fee_minor ?? 0)
  const [rows, setRows] = useState<Record<string, SheetMeterRow>>(() => {
    const base = { ...info.defaults }
    if (sheet?.mode === 'resource') {
      for (const k of Object.keys(sheet.meters)) base[k] = { ...base[k], ...sheet.meters[k] }
    }
    return base
  })
  const [confirmWarnings, setConfirmWarnings] = useState<BelowCostWarning[] | null>(null)
  const [publishing, setPublishing] = useState(false)

  const currency = info.currency
  const rateKey = currency === 'INR' ? 'rate_inr_minor' : 'rate_usd_minor'

  // Live below-cost detection against the plan's rate card (per unit block).
  const planCost = useCallback((meter: string): number => {
    const r = usage?.rate_card?.[meter]
    return r ? (r as unknown as Record<string, number>)[rateKey] ?? 0 : 0
  }, [usage, rateKey])

  const belowCost = useMemo(() => {
    if (mode === 'free') return []
    if (mode === 'per_user') {
      const cost = planCost('users')
      return perUser < cost && cost > 0
        ? [{ meter: 'users', label: 'Runtime users', your_rate_minor: perUser, platform_rate_minor: cost, currency }]
        : []
    }
    return METERS.filter((m) => {
      const cost = planCost(m)
      const planBlock = usage?.rate_card?.[m]?.unit_block || 1
      const yourBlock = rows[m]?.unit_block || 1
      if (cost <= 0) return false
      return (rows[m]?.rate ?? 0) / yourBlock < cost / planBlock
    }).map((m) => ({
      meter: m, label: rows[m]?.label ?? m,
      your_rate_minor: rows[m]?.rate ?? 0, platform_rate_minor: planCost(m), currency,
    }))
  }, [mode, perUser, rows, planCost, currency, usage])

  const setAllFromMargin = () => {
    const raw = window.prompt('Set every price from a margin over your plan cost. Margin %:',
      String(info.marketplace.default_margin_percent))
    if (raw === null) return
    const pct = parseFloat(raw)
    if (!Number.isFinite(pct)) { toast.error('Enter a number'); return }
    setRows((r) => {
      const next = { ...r }
      for (const m of METERS) {
        next[m] = { ...next[m], rate: Math.round(planCost(m) * (1 + pct / 100)) }
      }
      return next
    })
  }

  const doPublish = async (confirm: boolean) => {
    setPublishing(true)
    try {
      const body: PublishSheetBody = {
        mode,
        per_user_minor: mode === 'per_user' ? perUser : null,
        signup_fee_minor: signupFee > 0 ? signupFee : null,
        confirm_below_cost: confirm,
      }
      if (mode === 'resource') {
        body.meters = Object.fromEntries(METERS.map((m) => [m, {
          rate: rows[m]?.rate ?? 0,
          unit_block: rows[m]?.unit_block,
          allowance_qty: rows[m]?.allowance_qty ?? 0,
        }]))
      }
      const result = await publishPriceSheet(projectId, body)
      if (!result.published && result.requires_confirmation) {
        setConfirmWarnings(result.warnings)
        return
      }
      setConfirmWarnings(null)
      toast.success(`Price sheet v${result.sheet?.version} published`)
      onPublished()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Publish failed')
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <span style={h}>What other organizations pay</span>
          <div style={hint}>
            Your own organization always uses this app free. Changes apply from the next
            billing month; current customer organizations get {info.marketplace.price_change_notice_days} days&apos; notice.
            {sheet ? ` Current sheet: v${sheet.version}.` : ' No sheet published yet.'}
          </div>
        </div>
        <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
          {(['free', 'per_user', 'resource'] as Mode[]).map((m) => (
            <button
              key={m} type="button" onClick={() => setMode(m)}
              style={{
                padding: '5px 12px', fontSize: '0.8125rem', cursor: 'pointer', border: 'none',
                background: mode === m ? 'var(--color-accent-weak)' : 'transparent',
                color: mode === m ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                fontWeight: mode === m ? 600 : 400,
              }}
            >
              {m === 'free' ? 'Free' : m === 'per_user' ? 'Per user' : 'By resource usage'}
            </button>
          ))}
        </div>
      </div>

      {belowCost.length > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 12,
          background: 'var(--color-warning-weak)', border: '1px solid var(--color-warning)',
          borderRadius: 8, padding: '10px 12px',
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--color-warning)', flex: 'none', marginTop: 1 }} />
          <div>
            <div style={{ fontSize: '0.8425rem', fontWeight: 600, color: 'var(--color-warning)' }}>
              {belowCost.length} of {mode === 'per_user' ? 1 : METERS.length} rate{METERS.length > 1 ? 's' : ''} {belowCost.length > 1 ? 'are' : 'is'} below your platform cost
            </div>
            <div style={{ ...hint, marginTop: 2 }}>
              {belowCost.map((w) => `${w.label}: ${formatMoney(w.your_rate_minor, currency)} vs your cost ${formatMoney(w.platform_rate_minor, currency)}`).join(' · ')}
              {' — paid usage of this app loses you money. Publishing will ask you to confirm.'}
            </div>
          </div>
        </div>
      )}

      {mode === 'per_user' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <span style={{ fontSize: '0.875rem' }}>Price per external user / month</span>
          <MoneyInput value={perUser} onChange={setPerUser} />
          <span style={hint}>{currency} · 0 = free</span>
        </div>
      )}

      {mode === 'resource' && (
        <div style={{ overflowX: 'auto', marginTop: 14 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem', minWidth: 640 }}>
            <thead>
              <tr>
                {['Meter', 'Free allowance / org', 'Your cost (plan)', 'Your price', 'Per', 'Margin'].map((th) => (
                  <th key={th} style={{
                    textAlign: 'left', fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '.04em',
                    textTransform: 'uppercase', color: 'var(--color-text-secondary)',
                    padding: '6px 8px', borderBottom: '1px solid var(--color-border-strong)',
                  }}>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METERS.map((m) => {
                const row = rows[m]
                if (!row) return null
                const cost = planCost(m)
                const isBelow = belowCost.some((w) => w.meter === m)
                const margin = cost > 0 ? Math.round(((row.rate - cost) / cost) * 100) : null
                return (
                  <tr key={m} style={isBelow ? { background: 'var(--color-warning-weak)' } : undefined}>
                    <td style={{ padding: '7px 8px', fontWeight: 500, borderBottom: '1px solid var(--color-border)' }}>{row.label}</td>
                    <td style={{ padding: '7px 8px', borderBottom: '1px solid var(--color-border)' }}>
                      <input
                        type="number" min={0} style={{ width: 90 }}
                        value={row.allowance_qty}
                        onChange={(e) => setRows((r) => ({ ...r, [m]: { ...r[m], allowance_qty: parseFloat(e.target.value) || 0 } }))}
                      />
                    </td>
                    <td style={{ ...mono, padding: '7px 8px', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)' }}>
                      {formatMoney(cost, currency)}
                    </td>
                    <td style={{ padding: '7px 8px', borderBottom: '1px solid var(--color-border)' }}>
                      <MoneyInput
                        value={row.rate} warn={isBelow}
                        onChange={(v) => setRows((r) => ({ ...r, [m]: { ...r[m], rate: v } }))}
                      />
                    </td>
                    <td style={{ ...hint, padding: '7px 8px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--color-border)' }}>{UNIT_LABEL[m]}</td>
                    <td style={{ ...mono, padding: '7px 8px', fontWeight: 600, borderBottom: '1px solid var(--color-border)', color: isBelow ? 'var(--color-warning)' : 'var(--color-success)' }}>
                      {margin === null ? '—' : `${margin >= 0 ? '+' : ''}${margin}%`}
                      {isBelow && (
                        <span style={{
                          fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '.05em',
                          background: 'var(--color-warning)', color: '#fff',
                          borderRadius: 99, padding: '2px 7px', marginLeft: 8, textTransform: 'uppercase',
                        }}>Below cost</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
        <span style={{ fontSize: '0.875rem' }}>Public sign-up fee (one-time)</span>
        <MoneyInput value={signupFee} onChange={setSignupFee} />
        <span style={hint}>{currency} · what a public self-registrant pays at checkout; 0 = free</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        {mode === 'resource' && (
          <Button variant="secondary" size="sm" onClick={setAllFromMargin}>
            Set all from margin %…
          </Button>
        )}
        <Button variant="primary" size="sm" loading={publishing} onClick={() => void doPublish(false)}>
          Publish price sheet
        </Button>
      </div>

      <Modal
        open={confirmWarnings !== null}
        onClose={() => setConfirmWarnings(null)}
        title="Publish with a below-cost rate?"
        footer={(
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmWarnings(null)}>
              Go back &amp; fix prices
            </Button>
            <Button variant="primary" size="sm" loading={publishing} onClick={() => void doPublish(true)}>
              Publish anyway
            </Button>
          </>
        )}
      >
        <p style={{ fontSize: '0.875rem', margin: '0 0 10px' }}>
          This price sheet charges less than your plan charges you:
        </p>
        <div style={{ background: 'var(--color-surface-2)', borderRadius: 8, padding: '10px 12px', fontSize: '0.875rem' }}>
          {(confirmWarnings ?? []).map((w) => (
            <div key={w.meter} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span>{w.label}</span>
              <span style={mono}>
                {formatMoney(w.your_rate_minor, w.currency)} vs your cost {formatMoney(w.platform_rate_minor, w.currency)}
              </span>
            </div>
          ))}
        </div>
        <p style={{ ...hint, marginTop: 10 }}>
          You can publish anyway — for example to subsidize early customers. If your
          plan&apos;s rates change later, you&apos;ll be warned again.
        </p>
      </Modal>
    </div>
  )
}

function MoneyInput({ value, onChange, warn }: {
  value: number
  onChange: (minor: number) => void
  warn?: boolean
}) {
  return (
    <input
      type="number" min={0} step="0.01"
      value={value / 100}
      onChange={(e) => onChange(Math.round((parseFloat(e.target.value) || 0) * 100))}
      style={{
        width: 90,
        ...(warn ? { borderColor: 'var(--color-warning)', color: 'var(--color-warning)' } : {}),
      }}
    />
  )
}

// ── 3 · Earnings ────────────────────────────────────────────────────────────

function EarningsCard({ earnings }: { earnings: Earnings }) {
  const negative = earnings.margin_minor < 0
  return (
    <div style={card}>
      <span style={h}>This month&apos;s earnings</span>
      <div style={{ overflowX: 'auto', marginTop: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.875rem', minWidth: 560 }}>
          <thead>
            <tr>
              {['Organization', 'Usage (their share)', 'Billed', 'Platform fee', 'You receive'].map((th, i) => (
                <th key={th} style={{
                  textAlign: i >= 2 ? 'right' : 'left', fontSize: '0.8125rem', fontWeight: 600,
                  letterSpacing: '.04em', textTransform: 'uppercase',
                  color: 'var(--color-text-secondary)', padding: '6px 8px',
                  borderBottom: '1px solid var(--color-border-strong)',
                }}>{th}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {earnings.organizations.map((o) => (
              <tr key={o.grant_id}>
                <td style={{ padding: '7px 8px', fontWeight: 500, borderBottom: '1px solid var(--color-border)' }}>{o.tenant_id}</td>
                <td style={{ ...mono, padding: '7px 8px', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)' }}>{formatUsage(o.usage)}</td>
                <td style={{ ...mono, padding: '7px 8px', textAlign: 'right', borderBottom: '1px solid var(--color-border)' }}>{formatMoney(o.billed_minor, o.currency)}</td>
                <td style={{ ...mono, padding: '7px 8px', textAlign: 'right', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)' }}>−{formatMoney(o.platform_fee_minor, o.currency)}</td>
                <td style={{ ...mono, padding: '7px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--color-success)', borderBottom: '1px solid var(--color-border)' }}>{formatMoney(o.net_minor, o.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border-strong)', marginTop: 10, paddingTop: 8, alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Net into app wallet {formatMoney(earnings.net_earnings_minor, earnings.currency)} · cloud cost {formatMoney(earnings.cloud_cost_minor, earnings.currency)}
        </span>
        <span style={{ ...mono, fontSize: '0.9125rem', fontWeight: 600, color: negative ? 'var(--color-warning)' : 'var(--color-success)' }}>
          {negative ? '−' : '+'}{formatMoney(Math.abs(earnings.margin_minor), earnings.currency)} margin
        </span>
      </div>
      {negative && (
        <div style={{ ...hint, marginTop: 6 }}>
          Earnings credit the app wallet before the cloud-cost debit. This app currently
          prices below its infrastructure cost — raise prices or grow paid usage.
        </div>
      )}
    </div>
  )
}
